import { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { LogOut, LayoutDashboard, MessageSquare, CalendarDays, Briefcase, RefreshCw, FileText } from "lucide-react";
import BlogManager from "@/components/BlogManager";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "enquiries", label: "Enquiries", icon: MessageSquare },
  { key: "appointments", label: "Appointments", icon: CalendarDays },
  { key: "services", label: "Services", icon: Briefcase },
  { key: "blog", label: "Blog", icon: FileText },
];

export default function AdminDashboard() {
  const token = localStorage.getItem("kk_admin_token");
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [o, e, a, s] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/enquiries"),
        api.get("/admin/appointments"),
        api.get("/admin/services"),
      ]);
      setOverview(o.data);
      setEnquiries(e.data);
      setAppointments(a.data);
      setServices(s.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("kk_admin_token");
        nav("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    load();
  }, []);

  if (!token) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    localStorage.removeItem("kk_admin_token");
    localStorage.removeItem("kk_admin_user");
    nav("/admin/login");
  };

  const updateEnquiry = async (id, status_val) => {
    await api.patch(`/admin/enquiries/${id}?status_val=${encodeURIComponent(status_val)}`);
    load();
  };
  const updateAppointment = async (id, status_val) => {
    await api.patch(`/admin/appointments/${id}?status_val=${encodeURIComponent(status_val)}`);
    load();
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="hidden md:flex flex-col w-64 bg-slate-950 text-slate-200 p-6">
        <div className="h-display text-3xl text-white">KK Associates</div>
        <div className="text-xs uppercase tracking-widest text-blue-300 mt-1">Admin</div>
        <nav className="mt-10 space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                  tab === t.key ? "bg-blue-700 text-white" : "hover:bg-slate-900 text-slate-300"
                }`}
              >
                <Icon size={16} /> {t.label}
              </button>
            );
          })}
        </nav>
        <div className="mt-auto space-y-2">
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm hover:bg-slate-900 text-slate-300"
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm hover:bg-slate-900 text-slate-300"
          >
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 p-6 lg:p-10">
        <div className="md:hidden flex items-center justify-between mb-6">
          <div className="h-display text-2xl">KK Admin</div>
          <button onClick={logout} data-testid="admin-logout-mobile" className="text-sm text-slate-700">Logout</button>
        </div>

        <div className="md:hidden mb-6 flex gap-2 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 rounded-full text-sm border ${tab === t.key ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>{t.label}</button>
          ))}
        </div>

        {tab === "overview" && (
          <div>
            <h2 className="h-display text-3xl text-slate-900">Overview</h2>
            <p className="text-sm text-slate-500 mt-1">A quick snapshot of your business activity.</p>
            <div className="mt-6 grid grid-cols-2 lg:grid-cols-6 gap-4">
              {overview && [
                { k: "Total Enquiries", v: overview.total_enquiries },
                { k: "New Enquiries", v: overview.new_enquiries },
                { k: "Total Appointments", v: overview.total_appointments },
                { k: "Pending Appointments", v: overview.pending_appointments },
                { k: "Services Listed", v: overview.total_services },
                { k: "Blog Posts", v: overview.total_blogs || 0 },
              ].map((s, i) => (
                <div key={i} data-testid={`stat-${i}`} className="rounded-2xl bg-white border border-slate-200 p-5">
                  <div className="text-xs text-slate-500 uppercase tracking-widest">{s.k}</div>
                  <div className="mt-2 h-display text-3xl text-slate-900">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div>
            <h2 className="h-display text-3xl text-slate-900">Contact Enquiries</h2>
            <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-left">Service</th>
                    <th className="px-4 py-3 text-left">Message</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No enquiries yet.</td></tr>
                  )}
                  {enquiries.map((e) => (
                    <tr key={e.id} data-testid={`enq-row-${e.id}`} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-900 font-medium">{e.name}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{e.email}</div>
                        <a href={`https://wa.me/${(e.phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline">WA: {e.phone}</a>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{e.service_interest || "-"}</td>
                      <td className="px-4 py-3 text-slate-600 max-w-xs truncate">{e.message}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${e.status === "new" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-700"}`}>{e.status}</span></td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`enq-status-${e.id}`}
                          value={e.status}
                          onChange={(ev) => updateEnquiry(e.id, ev.target.value)}
                          className="text-xs border border-slate-200 rounded-md px-2 py-1"
                        >
                          <option value="new">new</option>
                          <option value="contacted">contacted</option>
                          <option value="closed">closed</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "appointments" && (
          <div>
            <h2 className="h-display text-3xl text-slate-900">Appointments</h2>
            <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 uppercase text-xs">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Contact</th>
                    <th className="px-4 py-3 text-left">Service</th>
                    <th className="px-4 py-3 text-left">When</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500">No appointments yet.</td></tr>
                  )}
                  {appointments.map((a) => (
                    <tr key={a.id} data-testid={`appt-row-${a.id}`} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-900 font-medium">{a.name}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{a.email}</div>
                        <a href={`https://wa.me/${(a.phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline">WA: {a.phone}</a>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{a.service}</td>
                      <td className="px-4 py-3 text-slate-600">{a.date} • {a.time}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${a.status === "pending" ? "bg-amber-50 text-amber-800" : a.status === "confirmed" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-700"}`}>{a.status}</span></td>
                      <td className="px-4 py-3">
                        <select
                          data-testid={`appt-status-${a.id}`}
                          value={a.status}
                          onChange={(ev) => updateAppointment(a.id, ev.target.value)}
                          className="text-xs border border-slate-200 rounded-md px-2 py-1"
                        >
                          <option value="pending">pending</option>
                          <option value="confirmed">confirmed</option>
                          <option value="completed">completed</option>
                          <option value="cancelled">cancelled</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "services" && (
          <div>
            <h2 className="h-display text-3xl text-slate-900">Services ({services.length})</h2>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((s) => (
                <div key={s.id} data-testid={`svc-row-${s.id}`} className="rounded-2xl bg-white border border-slate-200 p-5">
                  <div className="text-xs uppercase tracking-widest text-blue-700">{s.category}</div>
                  <div className="mt-1 font-medium text-slate-900">{s.title}</div>
                  <div className="mt-2 text-xs text-slate-500">/{s.slug}</div>
                  <div className="mt-3 flex gap-2 flex-wrap">
                    {s.is_featured && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">Featured</span>}
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{s.is_active ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "blog" && <BlogManager />}

        {loading && <div className="fixed top-4 right-4 text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1">Loading…</div>}
      </div>
    </div>
  );
}
